import { Box, RadioGroup, Slider, Stack, styled, Typography } from '@chronosphereio/chrono-ui';
import React from 'react';
import {
  DecreasingPriorityGridIcon,
  FeaturedChartGridIcon,
  FourColumnGridIcon,
  KeepExistingIcon,
  ThreeColumnGridIcon,
  TwoColumnGridIcon,
} from './LayoutPresetIcons';

const HeightPreviewBar = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  opacity: 0.1,
  width: '10%',
  borderRadius: '2px',
}));

export type LayoutPreset = 'keep-existing' | '2-col' | '3-col' | '4-col' | 'featured' | 'decreasing';

const LAYOUT_RADIO_VALUES: Array<{ value: LayoutPreset; label: React.ReactNode }> = [
  {
    value: 'keep-existing',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <KeepExistingIcon />
        <Typography>Keep existing layout</Typography>
      </Stack>
    ),
  },
  {
    value: '2-col',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <TwoColumnGridIcon />
        <Typography>Two column grid</Typography>
      </Stack>
    ),
  },
  {
    value: '3-col',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <ThreeColumnGridIcon />
        <Typography>Three column grid</Typography>
      </Stack>
    ),
  },
  {
    value: '4-col',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <FourColumnGridIcon />
        <Typography>Four column grid</Typography>
      </Stack>
    ),
  },
  {
    value: 'featured',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <FeaturedChartGridIcon />
        <Typography>Featured chart with three column grid</Typography>
      </Stack>
    ),
  },
  {
    value: 'decreasing',
    label: (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <DecreasingPriorityGridIcon />
        <Typography>Decreasing priority</Typography>
      </Stack>
    ),
  },
];

export interface CleanLayoutOptionsProps {
  selectedLayout: LayoutPreset;
  onSelectedLayoutChange: (preset: LayoutPreset) => void;
  panelHeight: number;
  onPanelHeightChange: (height: number) => void;
  panelHeightMin: number;
  panelHeightMax: number;
  gridRowHeightPx: number;
}

export function CleanLayoutOptions({
  selectedLayout,
  onSelectedLayoutChange,
  panelHeight,
  onPanelHeightChange,
  panelHeightMin,
  panelHeightMax,
  gridRowHeightPx,
}: CleanLayoutOptionsProps) {
  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h3">Panel group layout</Typography>
        <Stack spacing={2} sx={{ padding: (t) => t.spacing(0, 1) }}>
          <RadioGroup
            value={selectedLayout}
            onChange={(_, value) => onSelectedLayoutChange(value as LayoutPreset)}
            values={LAYOUT_RADIO_VALUES}
            direction="column"
          />
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="h3">Panel height</Typography>
        <Stack spacing={2}>
          <Box sx={{ py: 0.5, px: 3 }}>
            <Slider
              value={panelHeight}
              onChange={(_, value) => onPanelHeightChange(value as number)}
              min={panelHeightMin}
              max={panelHeightMax}
            />
          </Box>
          <Stack
            sx={{
              border: (t) => `1px dashed ${t.palette.border.default}`,
              height: `${panelHeight * gridRowHeightPx}px`,
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'absolute',
                bottom: 0,
                height: '100%',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'end',
                paddingBottom: 1,
              }}
            >
              <HeightPreviewBar sx={{ height: '20%' }} />
              <HeightPreviewBar sx={{ height: '30%' }} />
              <HeightPreviewBar sx={{ height: '25%' }} />
              <HeightPreviewBar sx={{ height: '15%' }} />
              <HeightPreviewBar sx={{ height: '20%' }} />
              <HeightPreviewBar sx={{ height: '30%' }} />
              <HeightPreviewBar sx={{ height: '20%' }} />
              <HeightPreviewBar sx={{ height: '30%' }} />
            </Stack>
            <Typography variant="caption">Panel height preview</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
